import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getInventoryOverview,
  getAllCustomerOrders,
  getAllNeededProducts,
  getUpdatedProducts,
  updateProductQuantity,
  updateProductStatus,
  updateAllProductsThreshold,
  updateProductThreshold,
  getAllProductType,
} from "../../redux/thunk";
import { resetUpdateQuantityStatus } from "../../redux/inventory/inventorySlice";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import Pagination from "../../components/Pagination";
import {
  IoSearchOutline,
  IoEyeOutline,
  IoAlertCircle,
  IoImageOutline,
  IoChevronDown,
  IoWarningOutline,
} from "react-icons/io5";
import type { InventoryProduct, NeededProduct } from "../../types/inventory";
import ConfirmPurchaseModal from "../../components/modals/ConfirmPurchaseModal";

const OVERVIEW_ITEMS_PER_PAGE = 20;

  
const NEEDED_ITEMS_PER_PAGE = 20;


const THRESHOLDS_ITEMS_PER_PAGE = 20;
const THRESHOLDS_FETCH_LIMIT = 10000;

const INV_PRODUCT_STORAGE_PREFIX = "inv-product-";

const RECENT_NEEDED_PURCHASES_KEY = "fb-admin-recent-needed-purchases";
const RECENT_NEEDED_MAX = 50;

interface RecentNeededPurchase {
  key: string;
  productId: string;
  productName: string;
  quantity: number;
  purchasedAt: string;
}

interface NeededPurchaseConfirm {
  product: InventoryProduct;
  quantityNeeded: number;
}

function loadRecentNeededPurchases(): RecentNeededPurchase[] {
  try {
    const raw = sessionStorage.getItem(RECENT_NEEDED_PURCHASES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentNeededPurchase[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveRecentNeededPurchases(items: RecentNeededPurchase[]) {
  try {
    sessionStorage.setItem(
      RECENT_NEEDED_PURCHASES_KEY,
      JSON.stringify(items.slice(0, RECENT_NEEDED_MAX))
    );
  } catch {
    /* ignore quota */
  }
}

function overviewProductExtras(
  product: InventoryProduct & Record<string, unknown>
) {
  const str = (k: string) =>
    typeof product[k] === "string" ? (product[k] as string).trim() : "";
  const imageUrl =
    str("product_image") ||
    str("productImage") ||
    str("image") ||
    str("imageUrl") ||
    str("thumbnail") ||
    str("photo");
  const size =
    str("size") ||
    str("package_size") ||
    str("packageSize") ||
    str("weight") ||
    "";
  const category = str("category") || str("product_category") || "";
  const location =
    str("state") ||
    str("stateLocation") ||
    str("state_location") ||
    str("stateName") ||
    str("state_name") ||
    str("location") ||
    str("warehouse") ||
    str("store_location") ||
    str("storeLocation") ||
    "";

  const normalizedLocation = (() => {
    const raw = String(location || "").trim();
    const l = raw.toLowerCase();
    if (!l) return "";
    if (l.includes("oyo")) return "oyo";
    if (l.includes("lagos")) return "lagos";
    return raw.toLowerCase();
  })();
  const locationLabel =
    normalizedLocation === "lagos"
      ? "Lagos"
      : normalizedLocation === "oyo"
        ? "Oyo"
        : "—";
  return {
    imageUrl,
    size: size || "—",
    category: category || "—",
    location: normalizedLocation || "—",
    locationLabel,
  };
}

function getProductThreshold(
  product: InventoryProduct & Record<string, unknown>,
): number | null {
  const raw =
    product.product_threshold ??
    product.productThreshold ??
    product.threshold ??
    product.autoShortage ??
    product.low_stock_threshold ??
    product.lowStockThreshold;
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function formatRecentNeededDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${date}, ${time}`;
  } catch {
    return iso;
  }
}

function neededProductToInventoryProduct(
  np: NeededProduct & Record<string, unknown>
): InventoryProduct {
  const qty = Number(np.quantity);
  const currentStockRaw =
    typeof np.current_stock === "number"
      ? np.current_stock
      : typeof np.currentStock === "number"
        ? np.currentStock
        : qty;
  const current_stock = Number.isFinite(currentStockRaw) ? currentStockRaw : 0;
  let stock_status: "Red" | "Yellow" | "Green" = "Green";
  if (current_stock <= 0) stock_status = "Red";
  else if (current_stock <= 10) stock_status = "Yellow";
  const updatedRaw =
    (typeof np.updatedAt === "string" && np.updatedAt) ||
    (typeof np.updated_at === "string" && np.updated_at) ||
    (typeof np.createdAt === "string" && np.createdAt) ||
    (typeof np.created_at === "string" && np.created_at) ||
    new Date().toISOString();
  return {
    product_id: np._id,
    product_name: np.name,
    current_stock,
    last_purchase_date: null,
    purchase_history: [],
    usage_history: [],
    delivery_records: [],
    stock_status,
    initial_stock: current_stock,
    updatedAt: updatedRaw,
  };
}

function formatNeededProductTimestamp(
  np: NeededProduct & Record<string, unknown>
): string {
  const raw =
    (typeof np.updatedAt === "string" && np.updatedAt) ||
    (typeof np.updated_at === "string" && np.updated_at) ||
    (typeof np.createdAt === "string" && np.createdAt) ||
    (typeof np.created_at === "string" && np.created_at) ||
    "";
  if (!raw) return "N/A";
  try {
    return new Date(raw).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return "N/A";
  }
}

type OverviewFilterDropdownKey = "category" | "status" | "state";

interface OverviewFilterOption {
  value: string;
  label: string;
}

const OVERVIEW_STATUS_FILTER_OPTIONS: OverviewFilterOption[] = [
  { value: "", label: "All Statuses" },
  { value: "Green", label: "Healthy Level" },
  { value: "Yellow", label: "Low Level" },
  { value: "Red", label: "Out of Stock" },
];

const OVERVIEW_STATE_FILTER_OPTIONS: OverviewFilterOption[] = [
  { value: "", label: "All States" },
  { value: "lagos", label: "Lagos" },
  { value: "oyo", label: "Oyo" },
];

function InventoryOverviewFilterDropdown({
  id,
  dropdownKey,
  openKey,
  setOpenKey,
  placeholder,
  value,
  options,
  onSelect,
  ariaLabel,
}: {
  id: string;
  dropdownKey: OverviewFilterDropdownKey;
  openKey: OverviewFilterDropdownKey | null;
  setOpenKey: (k: OverviewFilterDropdownKey | null) => void;
  placeholder: string;
  value: string;
  options: OverviewFilterOption[];
  onSelect: (value: string) => void;
  ariaLabel: string;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isOpen = openKey === dropdownKey;
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    setMenuPos({
      top: r.bottom + 8,
      left: r.left,
      width: r.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
  }, [isOpen, updateMenuPosition, options.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || menuRef.current?.contains(t)) {
        return;
      }
      setOpenKey(null);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, setOpenKey]);

  const selected = options.find((o) => o.value === value);
  const headerLabel = selected?.label ?? placeholder;

  const menuPortal =
    isOpen &&
    menuPos &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        ref={menuRef}
        role="presentation"
        className="overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/10"
        style={{
          position: "fixed",
          top: menuPos.top,
          left: menuPos.left,
          width: menuPos.width,
          zIndex: 9999,
        }}
      >
        <ul
          role="listbox"
          aria-labelledby={id}
          className="max-h-64 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <li
                key={`${dropdownKey}-${opt.value || "__all"}`}
                role="option"
                aria-selected={isSelected}
              >
                <button
                  type="button"
                  className={`w-full px-3 py-2.5 text-center text-sm text-gray-900 transition-colors hover:bg-gray-50 ${
                    isSelected ? "bg-gray-50 font-medium" : ""
                  }`}
                  onClick={() => {
                    onSelect(opt.value);
                    setOpenKey(null);
                  }}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>,
      document.body
    );

  return (
    <div className="relative min-w-[9.5rem] shrink-0 sm:min-w-[10.5rem]">
      <button
        ref={buttonRef}
        id={id}
        type="button"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => setOpenKey(isOpen ? null : dropdownKey)}
        className={`flex w-full items-center justify-center gap-2 rounded-md bg-[#c8e6d0] px-3 py-2.5 text-center text-sm font-medium text-gray-900 transition-colors hover:brightness-[0.98] ${
          isOpen ? "shadow-md ring-1 ring-black/10" : ""
        }`}
      >
        <span className="min-w-0 flex-1 truncate text-center">{headerLabel}</span>
        <IoChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>
      {menuPortal}
    </div>
  );
}

const SalesInventory: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<
    "overview" | "orders" | "needed" | "updated" | "thresholds"
  >("overview");

  // Inventory Overview states
  const [overviewPage, setOverviewPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<
    "" | "Red" | "Yellow" | "Green"
  >("");
  const [selectedState, setSelectedState] = useState("");
  const [overviewFilterDropdownOpen, setOverviewFilterDropdownOpen] =
    useState<OverviewFilterDropdownKey | null>(null);

  useEffect(() => {
    if (location.pathname.includes("/orders")) {
      setActiveTab("orders");
    } else if (location.pathname.includes("/needed")) {
      setActiveTab("needed");
    } else if (location.pathname.includes("/updated")) {
      setActiveTab("updated");
    } else if (location.pathname.includes("/thresholds")) {
      setActiveTab("thresholds");
    } else {
      setActiveTab("overview");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (activeTab !== "overview") {
      setOverviewFilterDropdownOpen(null);
    }
  }, [activeTab]);

  // Customer Orders states
  const [filterType, setFilterType] = useState("");
  const [customDate, setCustomDate] = useState("");
  const [customerOrdersPage, setCustomerOrdersPage] = useState(1);

  // Needed Products states
  const [neededProductsPage, setNeededProductsPage] = useState(1);
  const neededProductsLimit = NEEDED_ITEMS_PER_PAGE;

  // Updated Products states
  const [updatedProductsFilter, setUpdatedProductsFilter] = useState<
    "today" | "weekly" | "monthly" | ""
  >(""); // No default filter
  const [updatedProductsPage, setUpdatedProductsPage] = useState(1);

  const [thresholdsPage, setThresholdsPage] = useState(1);
  const [thresholdsSearchQuery, setThresholdsSearchQuery] = useState("");
  const [defaultThreshold, setDefaultThreshold] = useState("");
  const [savingDefaultThreshold, setSavingDefaultThreshold] = useState(false);
  const [perProductThresholds, setPerProductThresholds] = useState<
    Record<string, string>
  >({});
  const [savingProductThresholdId, setSavingProductThresholdId] = useState<
    string | null
  >(null);

  const [confirmNeededPurchase, setConfirmNeededPurchase] =
    useState<NeededPurchaseConfirm | null>(null);
  const [recentNeededPurchases, setRecentNeededPurchases] = useState<
    RecentNeededPurchase[]
  >(() => loadRecentNeededPurchases());

  // Customer order details modal
  const [selectedCustomerOrder, setSelectedCustomerOrder] = useState<any>(null);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);

  const {
    inventoryOverview,
    customerOrders,
    neededProducts,
    updatedProducts,
    updateQuantity,
  } = useAppSelector((state) => state.inventory);

  const productCategories = useAppSelector(
    (state) => (state as any).products?.categories
  );

  useEffect(() => {
    if (activeTab === "overview") {
      const overviewFiltersActive =
        Boolean(searchQuery.trim()) ||
        Boolean(selectedCategory) ||
        Boolean(selectedStatus) ||
        Boolean(selectedState);

      dispatch(
        getInventoryOverview({
          page: overviewFiltersActive ? 1 : overviewPage,
          limit: overviewFiltersActive ? THRESHOLDS_FETCH_LIMIT : OVERVIEW_ITEMS_PER_PAGE,
        })
      );
      // dropdown data sources
      dispatch(getAllProductType());
    } else if (activeTab === "orders") {
      // Fetch orders based on filter
      if (filterType === "custom") {
        // Only fetch if custom date is selected
        if (customDate) {
          dispatch(getAllCustomerOrders(customDate));
        }
      } else {
        // Fetch with the selected filter or empty string for all orders
        dispatch(getAllCustomerOrders(filterType));
      }
    } else if (activeTab === "needed") {
      dispatch(
        getAllNeededProducts({
          page: neededProductsPage,
          limit: neededProductsLimit,
        })
      );
    } else if (activeTab === "updated") {
      dispatch(
        getUpdatedProducts({
          filter: updatedProductsFilter || undefined,
        })
      );
    } else if (activeTab === "thresholds") {
      dispatch(
        getInventoryOverview({
          page: 1,
          limit: THRESHOLDS_FETCH_LIMIT,
        })
      );
    }
  }, [
    activeTab,
    overviewPage,
    searchQuery,
    selectedCategory,
    selectedStatus,
    selectedState,
    filterType,
    customDate,
    neededProductsPage,
    neededProductsLimit,
    updatedProductsFilter,
    updatedProductsPage,
    dispatch,
  ]);

  useEffect(() => {
    saveRecentNeededPurchases(recentNeededPurchases);
  }, [recentNeededPurchases]);

  useEffect(() => {
    if (updateQuantity.status === "success") {
      dispatch(resetUpdateQuantityStatus());
      if (activeTab === "overview") {
        dispatch(
          getInventoryOverview({
            page: overviewPage,
            limit: OVERVIEW_ITEMS_PER_PAGE,
          })
        );
      } else if (activeTab === "needed") {
        dispatch(
          getAllNeededProducts({
            page: neededProductsPage,
            limit: neededProductsLimit,
          })
        );
      } else if (activeTab === "thresholds") {
        dispatch(
          getInventoryOverview({
            page: 1,
            limit: THRESHOLDS_FETCH_LIMIT,
          })
        );
      }
    }
  }, [
    updateQuantity.status,
    dispatch,
    activeTab,
    overviewPage,
    neededProductsPage,
    neededProductsLimit,
  ]);

  const getStockStatusColor = (status: string) => {
    switch (status) {
      case "Red":
        return "text-red-600 bg-red-50";
      case "Yellow":
        return "text-amber-800 bg-amber-50";
      case "Green":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getStockStatusText = (status: string) => {
    switch (status) {
      case "Red":
        return "Out of Stock";
      case "Yellow":
        return "Low Level";
      case "Green":
        return "Healthy Level";
      default:
        return status;
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      return date.toLocaleDateString("en-US", options);
    } catch (e) {
      return "Invalid Date";
    }
  };

  const handleViewOrderDetails = (order: any) => {
    setSelectedCustomerOrder(order);
    setShowOrderDetailsModal(true);
  };

  const closeOrderDetailsModal = () => {
    setShowOrderDetailsModal(false);
    setSelectedCustomerOrder(null);
  };

  const persistProductForDetail = (product: InventoryProduct) => {
    try {
      sessionStorage.setItem(
        `${INV_PRODUCT_STORAGE_PREFIX}${product.product_id}`,
        JSON.stringify(product)
      );
    } catch {
      /* ignore */
    }
  };

  // Filter inventory overview by search
  const filteredOverviewData = inventoryOverview.data.filter((p) => {
    const product = p as InventoryProduct & Record<string, unknown>;
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch = !q
      ? true
      : product.product_name.toLowerCase().includes(q);

    const { category, location } = overviewProductExtras(product);

    const matchesCategory =
      !selectedCategory ||
      category.toLowerCase() === selectedCategory.toLowerCase();
    const matchesState = !selectedState || location === selectedState;
    const matchesStatus =
      !selectedStatus || product.stock_status === selectedStatus;

    return matchesSearch && matchesCategory && matchesState && matchesStatus;
  });

  const overviewFiltersActive =
    Boolean(searchQuery.trim()) ||
    Boolean(selectedCategory) ||
    Boolean(selectedStatus) ||
    Boolean(selectedState);

  const overviewDisplayTotalPages = overviewFiltersActive
    ? Math.max(1, Math.ceil(filteredOverviewData.length / OVERVIEW_ITEMS_PER_PAGE))
    : inventoryOverview.totalPages;

  const paginatedOverviewRows = overviewFiltersActive
    ? filteredOverviewData.slice(
        (overviewPage - 1) * OVERVIEW_ITEMS_PER_PAGE,
        (overviewPage - 1) * OVERVIEW_ITEMS_PER_PAGE + OVERVIEW_ITEMS_PER_PAGE
      )
    : filteredOverviewData;

  const filteredThresholdsData = useMemo(() => {
    const q = thresholdsSearchQuery.trim().toLowerCase();
    return inventoryOverview.data.filter((p) => {
      if (!q) return true;
      return p.product_name.toLowerCase().includes(q);
    });
  }, [inventoryOverview.data, thresholdsSearchQuery]);

  const thresholdsDisplayTotalPages = Math.max(
    1,
    Math.ceil(filteredThresholdsData.length / THRESHOLDS_ITEMS_PER_PAGE)
  );

  const paginatedThresholdsRows = useMemo(() => {
    const start = (thresholdsPage - 1) * THRESHOLDS_ITEMS_PER_PAGE;
    return filteredThresholdsData.slice(
      start,
      start + THRESHOLDS_ITEMS_PER_PAGE
    );
  }, [filteredThresholdsData, thresholdsPage]);

  useEffect(() => {
    if (thresholdsPage > thresholdsDisplayTotalPages) {
      setThresholdsPage(thresholdsDisplayTotalPages);
    }
  }, [thresholdsDisplayTotalPages, thresholdsPage]);

  const overviewCategoryOptions = useMemo((): OverviewFilterOption[] => {
    const raw = Array.isArray(productCategories)
      ? productCategories
      : Array.isArray((productCategories as { data?: unknown })?.data)
        ? (productCategories as { data: unknown[] }).data
        : [];
    const labels = raw
      .map((c: { type?: string; name?: string }) =>
        String(c?.type || c?.name || "").trim()
      )
      .filter(Boolean);
    const uniq = Array.from(new Set(labels)).sort((a, b) =>
      a.localeCompare(b)
    );
    return [
      { value: "", label: "All Categories" },
      ...uniq.map((label) => ({ value: label, label })),
    ];
  }, [productCategories]);
  const overviewStateOptions = OVERVIEW_STATE_FILTER_OPTIONS;

  const renderOverviewTab = () => (
    <div className="px-6 pb-2">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/95">
                <th
                  scope="col"
                  className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Image
                </th>
                <th
                  scope="col"
                  className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Product Name
                </th>
                <th
                  scope="col"
                  className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Category
                </th>
                <th
                  scope="col"
                  className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Size
                </th>
                <th
                  scope="col"
                  className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Location
                </th>
                <th
                  scope="col"
                  className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Current Stock
                </th>
                <th
                  scope="col"
                  className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap"
                >
                  Last Purchase
                </th>
                <th
                  scope="col"
                  className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {inventoryOverview.status === "loading" ? (
                <tr>
                  <td colSpan={9} className="p-12 text-center">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : inventoryOverview.data.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="p-12 text-center text-gray-500 italic"
                  >
                    No products found!
                  </td>
                </tr>
              ) : paginatedOverviewRows.length > 0 ? (
                paginatedOverviewRows.map((product) => {
                  const ext = product as InventoryProduct &
                    Record<string, unknown>;
                  const { imageUrl, size, category, locationLabel } =
                    overviewProductExtras(ext);
                  return (
                    <tr
                      className="border-b border-gray-100 transition-colors hover:bg-gray-50/70"
                      key={product.product_id}
                    >
                      <td className="px-5 py-4 align-middle">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt=""
                            className="h-10 w-10 rounded-md border border-gray-100 object-cover"
                          />
                        ) : (
                          <div
                            className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-100 bg-gray-50 text-gray-400"
                            aria-hidden
                          >
                            <IoImageOutline className="h-5 w-5" />
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 align-middle font-semibold text-gray-900 whitespace-nowrap">
                        {product.product_name}
                      </td>
                      <td className="px-5 py-4 align-middle text-gray-600">
                        {category}
                      </td>
                      <td className="px-5 py-4 align-middle text-gray-600 whitespace-nowrap">
                        {size}
                      </td>
                      <td className="px-5 py-4 align-middle text-gray-600 whitespace-nowrap">
                        {locationLabel}
                      </td>
                      <td className="px-5 py-4 align-middle tabular-nums text-gray-800">
                        {product.current_stock}
                      </td>
                      <td className="px-5 py-4 align-middle whitespace-nowrap text-gray-600">
                        {formatDate(product.last_purchase_date)}
                      </td>
                      <td className="px-5 py-4 align-middle">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getStockStatusColor(
                            product.stock_status
                          )}`}
                        >
                          {getStockStatusText(product.stock_status)}
                        </span>
                      </td>
                      <td className="px-5 py-4 align-middle text-right">
                        <Link
                          to={`/dashboard/sales-inventory/product/${product.product_id}`}
                          state={{ product }}
                          onClick={() => persistProductForDetail(product)}
                          className="inline-flex items-center justify-end gap-1.5 font-medium text-gray-700 hover:text-gray-900"
                        >
                          <span>View</span>
                          <IoEyeOutline className="h-4 w-4 shrink-0" />
                        </Link>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={9}
                    className="p-12 text-center text-gray-500 italic"
                  >
                    No products match your search
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderOrdersTab = () => {
    // Pagination for customer orders
    const itemsPerPage = 20;
    const indexOfLastOrder = customerOrdersPage * itemsPerPage;
    const indexOfFirstOrder = indexOfLastOrder - itemsPerPage;
    const currentOrders = customerOrders.data.slice(
      indexOfFirstOrder,
      indexOfLastOrder
    );

    return (
      <div className="overflow-y-auto">
        <table className="w-full" style={{ minWidth: "1000px" }}>
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-2">S/N</th>
              <th className="p-2">Customer Name</th>
              <th className="p-2">Email</th>
              <th className="p-2 text-nowrap">Total Products</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {customerOrders.status === "loading" ? (
              <tr>
                <td colSpan={5} className="p-10 text-center">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : customerOrders.data.length === 0 ? (
              <tr className="text-center">
                <td colSpan={5} className="p-10 text-secondary">
                  No orders found!
                </td>
              </tr>
            ) : (
              currentOrders.map((order, index) => (
                <tr className="border-b" key={order.userId}>
                  <td className="text-secondary p-2">
                    {indexOfFirstOrder + index + 1}
                  </td>
                  <td className="p-2 text-nowrap">{order.userName}</td>
                  <td className="p-2">{order.email}</td>
                  <td className="p-2 text-center font-semibold">
                    {order.products.length}
                  </td>
                  <td className="p-2">
                    <button
                      onClick={() => handleViewOrderDetails(order)}
                      className="text-blue-500 hover:underline focus:outline-none"
                    >
                      View Products
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderNeededProductsTab = () => {
    let urgentTotal = 0;
    if (neededProducts.status === "success") {
      urgentTotal =
        neededProducts.totalProducts || neededProducts.count || 0;
      if (urgentTotal === 0 && neededProducts.data.length > 0) {
        urgentTotal = neededProducts.data.length;
      }
    }
    const showUrgentBanner =
      neededProducts.status === "success" && urgentTotal > 0;

    return (
      <div className="px-6 pb-2">
        {showUrgentBanner && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-4 sm:px-5">
            <div className="flex gap-3">
              <IoAlertCircle
                className="h-6 w-6 shrink-0 text-red-600"
                aria-hidden
              />
              <div className="min-w-0">
                <p className="font-semibold text-red-800">
                  Urgent: {urgentTotal} Item{urgentTotal === 1 ? "" : "s"} Need
                  Procurement
                </p>
                <p className="mt-1 text-sm text-red-700 leading-relaxed">
                  These items are required for pending orders. Please procure and
                  mark as purchased to update stock / out stock and low level.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4 bg-gray-50/80">
            <h2 className="text-lg font-semibold text-gray-900">
              Pending Procurement
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Items waiting to be purchased
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-600">
                  <th className="px-5 py-3 font-medium">Product Name</th>
                  <th className="px-5 py-3 font-medium">Quantity Needed</th>
                  <th className="px-5 py-3 font-medium">Time Stamp</th>
                  <th className="px-5 py-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {neededProducts.status === "loading" ? (
                  <tr>
                    <td colSpan={4} className="p-12 text-center">
                      <LoadingSpinner />
                    </td>
                  </tr>
                ) : neededProducts.data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="p-12 text-center text-gray-500 italic"
                    >
                      No products need restocking.
                    </td>
                  </tr>
                ) : (
                  neededProducts.data.map((product) => {
                    const ext = product as NeededProduct &
                      Record<string, unknown>;
                    return (
                      <tr
                        className="border-t border-gray-100 hover:bg-gray-50/60"
                        key={product._id}
                      >
                        <td className="px-5 py-4 font-medium text-gray-900 whitespace-nowrap">
                          {product.name}
                        </td>
                        <td className="px-5 py-4 text-gray-800">
                          {product.quantity}
                        </td>
                        <td className="px-5 py-4 text-gray-600 whitespace-nowrap">
                          {formatNeededProductTimestamp(ext)}
                        </td>
                        <td className="px-5 py-4">
                          <button
                            type="button"
                            onClick={() => {
                              const q = Number(ext.quantity);
                              const quantityNeeded =
                                Number.isFinite(q) && q >= 1
                                  ? Math.floor(q)
                                  : 1;
                              setConfirmNeededPurchase({
                                product: neededProductToInventoryProduct(ext),
                                quantityNeeded,
                              });
                            }}
                            className="rounded-lg bg-green-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-800 transition-colors"
                          >
                            Mark as Purchased
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {neededProducts.status === "success" &&
            (neededProducts.totalProducts ?? neededProducts.count ?? 0) >
              0 && (
              <div className="border-t border-gray-100 px-5 py-4">
                <Pagination
                  length={neededProducts.totalProducts ?? neededProducts.count ?? 0}
                  itemsPerPage={neededProductsLimit}
                  handlePagination={(page) => setNeededProductsPage(page)}
                  currentPage={neededProductsPage}
                  prevPage={() =>
                    setNeededProductsPage((prev) => Math.max(1, prev - 1))
                  }
                  nextPage={() =>
                    setNeededProductsPage((prev) =>
                      Math.min(
                        Math.max(1, neededProducts.totalPages),
                        prev + 1,
                      ),
                    )
                  }
                  responseTotalPages={neededProducts.totalPages}
                />
              </div>
            )}
        </div>

        <div className="mt-8 rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4 bg-gray-50/80">
            <h2 className="text-lg font-semibold text-gray-900">
              Recently Purchased
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Items that have been marked as purchased
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-600">
                  <th className="px-5 py-3 font-medium">Product Name</th>
                  <th className="px-5 py-3 font-medium">Quantity</th>
                  <th className="px-5 py-3 font-medium">Time Stamp</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentNeededPurchases.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="p-12 text-center text-gray-500 italic"
                    >
                      No items marked as purchased yet.
                    </td>
                  </tr>
                ) : (
                  recentNeededPurchases.map((row) => (
                    <tr
                      className="border-t border-gray-100 hover:bg-gray-50/60"
                      key={row.key}
                    >
                      <td className="px-5 py-4 font-medium text-gray-900 whitespace-nowrap">
                        {row.productName}
                      </td>
                      <td className="px-5 py-4 text-gray-800">
                        {row.quantity}
                      </td>
                      <td className="px-5 py-4 text-gray-600 whitespace-nowrap">
                        {formatRecentNeededDateTime(row.purchasedAt)}
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex rounded-full px-3 py-1 text-xs font-semibold text-green-800 bg-green-100">
                          Purchased
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderUpdatedProductsTab = () => {
    // Client-side pagination since backend doesn't support it
    const itemsPerPage = 50;
    const indexOfLastProduct = updatedProductsPage * itemsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - itemsPerPage;
    const currentProducts = updatedProducts.data.slice(
      indexOfFirstProduct,
      indexOfLastProduct
    );

    return (
      <div className="overflow-y-auto">
        <table className="w-full" style={{ minWidth: "1200px" }}>
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-2">S/N</th>
              <th className="p-2">Product Name</th>
              <th className="p-2 text-nowrap">Initial Quantity</th>
              <th className="p-2 text-nowrap">Current Quantity</th>
              <th className="p-2 text-nowrap">Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {updatedProducts.status === "loading" ? (
              <tr>
                <td colSpan={5} className="p-10 text-center">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : updatedProducts.data.length === 0 ? (
              <tr className="text-center">
                <td colSpan={5} className="p-10 text-secondary">
                  No products updated in this period!
                </td>
              </tr>
            ) : (
              currentProducts.map((product, index) => (
                <tr className="border-b" key={product._id}>
                  <td className="text-secondary p-2">
                    {indexOfFirstProduct + index + 1}
                  </td>
                  <td className="p-2 text-nowrap">{product.name}</td>
                  <td className="p-2 text-center text-gray-600">
                    {product.initialQuantity}
                  </td>
                  <td className="p-2 text-center font-semibold">
                    {product.quantity}
                  </td>
                  <td className="p-2 text-nowrap">
                    {new Date(product.updatedAt).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderThresholdsTab = () => {
    return (
      <div className="px-6 pb-2">
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">
            Default Threshold
          </h2>
          <p className="text-sm text-gray-500 mt-1 mb-6">
            Set a single threshold value that applies to all products. Products
            with stock at or below this number will be flagged as low stock.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="flex-1 max-w-xs">
              <label
                htmlFor="default-threshold-input"
                className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"
              >
                <IoWarningOutline className="w-4 h-4 text-yellow-600" />
                Threshold Value
              </label>
              <input
                id="default-threshold-input"
                type="number"
                min={0}
                step={1}
                value={defaultThreshold}
                onChange={(e) => setDefaultThreshold(e.target.value)}
                placeholder="e.g. 10"
                className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-gray-900 outline-none focus:ring-1 focus:ring-green-600 focus:border-green-600"
              />
            </div>
            <button
              type="button"
              disabled={savingDefaultThreshold || !defaultThreshold.trim()}
              onClick={async () => {
                const val = parseInt(defaultThreshold.trim(), 10);
                if (Number.isNaN(val) || val < 0) return;
                setSavingDefaultThreshold(true);
                try {
                  await dispatch(
                    updateAllProductsThreshold({ threshold: val })
                  ).unwrap();
                } catch {
                  /* thunk toasts error */
                } finally {
                  setSavingDefaultThreshold(false);
                }
              }}
              className="h-[48px] bg-green-700 text-white px-6 rounded-lg text-sm font-semibold hover:bg-green-800 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {savingDefaultThreshold ? "Saving\u2026" : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Product Thresholds
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                View threshold values per product. Use the default threshold
                above to update all products at once.
              </p>
            </div>
            <div className="relative w-full sm:w-64 shrink-0">
              <input
                type="search"
                placeholder="Search Products"
                value={thresholdsSearchQuery}
                onChange={(e) => {
                  setThresholdsSearchQuery(e.target.value);
                  setThresholdsPage(1);
                }}
                className="w-full pl-4 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600"
              />
              <IoSearchOutline className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left min-w-[900px]">
              <thead className="bg-white border-b border-gray-100 text-gray-500">
                <tr>
                  <th className="px-6 py-4 font-medium">Image</th>
                  <th className="px-6 py-4 font-medium">Name</th>
                  <th className="px-6 py-4 font-medium">Category</th>
                  <th className="px-6 py-4 font-medium">Size</th>
                  <th className="px-6 py-4 font-medium">Current Stock</th>
                  <th className="px-6 py-4 font-medium">Current Threshold</th>
                  <th className="px-6 py-4 font-medium">New Threshold</th>
                  <th className="px-6 py-4 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {inventoryOverview.status === "loading" ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <LoadingSpinner />
                    </td>
                  </tr>
                ) : filteredThresholdsData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-12 text-center text-gray-500 italic"
                    >
                      {inventoryOverview.data.length === 0
                        ? "No products loaded."
                        : "No products match your search."}
                    </td>
                  </tr>
                ) : (
                  paginatedThresholdsRows.map((product) => {
                    const ext = product as InventoryProduct &
                      Record<string, unknown>;
                    const { imageUrl, size, category } =
                      overviewProductExtras(ext);
                    const currentThreshold = getProductThreshold(ext);
                    const editedValue =
                      perProductThresholds[product.product_id];
                    const hasEdit = editedValue !== undefined;
                    const inputValue = hasEdit ? editedValue : "";
                    const isSaving =
                      savingProductThresholdId === product.product_id;
                    return (
                      <tr
                        key={product.product_id}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt=""
                              className="h-10 w-10 rounded-md border border-gray-100 object-cover"
                            />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-100 bg-gray-50 text-gray-400">
                              <IoImageOutline className="h-5 w-5" />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                          {product.product_name}
                        </td>
                        <td className="px-6 py-4 text-gray-600">{category}</td>
                        <td className="px-6 py-4 text-gray-600">{size}</td>
                        <td className="px-6 py-4 tabular-nums text-gray-800">
                          {product.current_stock}
                        </td>
                        {/* Read-only: previously saved threshold */}
                        <td className="px-6 py-4 tabular-nums">
                          {currentThreshold != null ? (
                            <span
                              className={`inline-flex items-center justify-center min-w-[2.5rem] rounded-full px-2.5 py-1 text-xs font-semibold ${
                                product.current_stock <= currentThreshold
                                  ? "bg-red-50 text-red-700"
                                  : "bg-green-50 text-green-700"
                              }`}
                            >
                              {currentThreshold}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-xs">
                              Not set
                            </span>
                          )}
                        </td>
                        {/* Editable: new threshold override */}
                        <td className="px-6 py-4">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={inputValue}
                            onChange={(e) =>
                              setPerProductThresholds((prev) => ({
                                ...prev,
                                [product.product_id]: e.target.value,
                              }))
                            }
                            placeholder={
                              currentThreshold != null
                                ? String(currentThreshold)
                                : "—"
                            }
                            className="w-20 p-2 text-center border border-gray-200 focus:border-green-600 rounded outline-none bg-white transition-colors tabular-nums"
                          />
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            type="button"
                            disabled={!hasEdit || !editedValue?.trim() || isSaving}
                            onClick={async () => {
                              const val = parseInt(editedValue?.trim() ?? "", 10);
                              if (Number.isNaN(val) || val < 0) return;
                              setSavingProductThresholdId(product.product_id);
                              try {
                                await dispatch(
                                  updateProductThreshold({
                                    productId: product.product_id,
                                    threshold: val,
                                  })
                                ).unwrap();
                                setPerProductThresholds((prev) => {
                                  const next = { ...prev };
                                  delete next[product.product_id];
                                  return next;
                                });
                              } catch {
                                /* thunk toasts error */
                              } finally {
                                setSavingProductThresholdId(null);
                              }
                            }}
                            className="bg-green-700 text-white px-4 py-2 rounded-md text-xs font-semibold hover:bg-green-800 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSaving ? "Saving\u2026" : "Save"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {filteredThresholdsData.length > 0 && (
            <div className="flex flex-col gap-3 border-t border-gray-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-gray-500">
                Showing {(thresholdsPage - 1) * THRESHOLDS_ITEMS_PER_PAGE + 1}{" "}
                -{" "}
                {Math.min(
                  thresholdsPage * THRESHOLDS_ITEMS_PER_PAGE,
                  filteredThresholdsData.length
                )}{" "}
                of {filteredThresholdsData.length} products (20 per page)
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setThresholdsPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={thresholdsPage === 1}
                  className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setThresholdsPage((prev) =>
                      Math.min(thresholdsDisplayTotalPages, prev + 1)
                    )
                  }
                  disabled={thresholdsPage === thresholdsDisplayTotalPages}
                  className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <main className="overflow-x-auto w-full">
      <div className="bg-white rounded-md shadow-md pb-6">
        <div className="w-full overflow-x-auto">
          {/* Header Section */}
          <div
            className={
              activeTab === "overview" ? "px-6 pt-6 pb-6" : "p-6 pb-2"
            }
            style={{ minWidth: "700px" }}
          >
            {activeTab === "overview" ? (
              <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between md:gap-6">
                <div className="min-w-0 shrink-0">
                  <h1 className="text-2xl font-bold">Inventory Overview</h1>
                  <p className="mt-1 text-sm text-gray-500">
                    Track and manage all products in real-time
                  </p>
                </div>
                <div className="flex min-w-0 max-w-full flex-nowrap items-center justify-end gap-2 overflow-x-auto py-0.5 md:gap-3 [scrollbar-width:thin]">
                  <div className="relative w-52 min-w-[11rem] shrink-0 sm:w-56">
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setOverviewPage(1);
                      }}
                      placeholder="Search Products"
                      className="w-full rounded-md border border-gray-200 p-2.5 pr-10 text-sm outline-none focus:border-[#B5572C] focus:ring-1 focus:ring-[#B5572C]"
                    />
                    <IoSearchOutline className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  </div>
                  <InventoryOverviewFilterDropdown
                    id="inv-overview-filter-category"
                    dropdownKey="category"
                    openKey={overviewFilterDropdownOpen}
                    setOpenKey={setOverviewFilterDropdownOpen}
                    placeholder="All Categories"
                    value={selectedCategory}
                    options={overviewCategoryOptions}
                    onSelect={(v) => {
                      setSelectedCategory(v);
                      setOverviewPage(1);
                    }}
                    ariaLabel="Filter by category"
                  />
                  <InventoryOverviewFilterDropdown
                    id="inv-overview-filter-status"
                    dropdownKey="status"
                    openKey={overviewFilterDropdownOpen}
                    setOpenKey={setOverviewFilterDropdownOpen}
                    placeholder="All Statuses"
                    value={selectedStatus}
                    options={OVERVIEW_STATUS_FILTER_OPTIONS}
                    onSelect={(v) => {
                      setSelectedStatus(v as "" | "Red" | "Yellow" | "Green");
                      setOverviewPage(1);
                    }}
                    ariaLabel="Filter by status"
                  />
                  <InventoryOverviewFilterDropdown
                    id="inv-overview-filter-state"
                    dropdownKey="state"
                    openKey={overviewFilterDropdownOpen}
                    setOpenKey={setOverviewFilterDropdownOpen}
                    placeholder="All States"
                    value={selectedState}
                    options={overviewStateOptions}
                    onSelect={(v) => {
                      setSelectedState(v);
                      setOverviewPage(1);
                    }}
                    ariaLabel="Filter by state"
                  />
                </div>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold">
                  {activeTab === "orders" && "Customer Orders"}
                  {activeTab === "needed" && "Items Needed for Procurement"}
                  {activeTab === "updated" && "Recently Updated Products"}
                  {activeTab === "thresholds" && "Stock Threshold Settings"}
                </h1>
                {activeTab === "needed" && (
                  <p className="mt-1 text-sm text-gray-500">
                    Priority action page for restocking — items triggering
                    procurement alerts
                  </p>
                )}
                {activeTab === "thresholds" && (
                  <p className="mt-1 text-sm text-gray-500">
                    Configure per-product stock level thresholds
                  </p>
                )}
              </>
            )}
          </div>

          {/* Filters Section */}
          <div
            className={`flex items-center justify-between px-6 ${
              activeTab === "orders" || activeTab === "updated"
                ? "pb-6"
                : "pb-0"
            }`}
            style={{ minWidth: "700px" }}
          >
            {/* Filter for Customer Orders */}
            {activeTab === "orders" && (
              <div className="flex items-center gap-4">
                <label htmlFor="filterType">
                  Filter by:
                  <select
                    name="filterType"
                    id="filterType"
                    className="border p-2 ml-4 rounded"
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value);
                      if (e.target.value !== "custom") {
                        setCustomDate("");
                      }
                    }}
                  >
                    <option value="">All Orders</option>
                    <option value="today">Today</option>
                    <option value="last3days">Last 3 Days</option>
                    <option value="custom">Custom Date</option>
                  </select>
                </label>
                {filterType === "custom" && (
                  <input
                    type="date"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    className="border p-2 rounded"
                    placeholder="Select date"
                  />
                )}
              </div>
            )}

            {/* Filter for Updated Products */}
            {activeTab === "updated" && (
              <label htmlFor="updatedFilter">
                Filter by:
                <select
                  name="updatedFilter"
                  id="updatedFilter"
                  className="border p-2 ml-4 rounded"
                  value={updatedProductsFilter}
                  onChange={(e) =>
                    setUpdatedProductsFilter(
                      e.target.value as "today" | "weekly" | "monthly" | ""
                    )
                  }
                >
                  <option value="">All</option>
                  <option value="today">Today</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </label>
            )}
          </div>

          {/* Tabs Navigation Removed - Handled by Sidebar */}

          {/* Tab Content */}
          <section className="overflow-y-auto">
            {activeTab === "overview" && renderOverviewTab()}
            {activeTab === "orders" && renderOrdersTab()}
            {activeTab === "needed" && renderNeededProductsTab()}
            {activeTab === "updated" && renderUpdatedProductsTab()}
            {activeTab === "thresholds" && renderThresholdsTab()}
          </section>
        </div>

        {/* Pagination */}
        {activeTab === "overview" && inventoryOverview.data.length > 0 && (
          <section className="p-6 flex items-center justify-between border-t border-gray-100">
            <div className="text-sm text-gray-500">
              Showing {(overviewPage - 1) * OVERVIEW_ITEMS_PER_PAGE + 1} -{" "}
              {Math.min(
                overviewPage * OVERVIEW_ITEMS_PER_PAGE,
                overviewFiltersActive
                  ? filteredOverviewData.length
                  : inventoryOverview.totalProducts
              )}{" "}
              of{" "}
              {overviewFiltersActive
                ? filteredOverviewData.length
                : inventoryOverview.totalProducts}{" "}
              products
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOverviewPage((prev) => Math.max(1, prev - 1))}
                disabled={overviewPage === 1}
                className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setOverviewPage((prev) =>
                    Math.min(overviewDisplayTotalPages, prev + 1)
                  )
                }
                disabled={overviewPage === overviewDisplayTotalPages}
                className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </section>
        )}

        {activeTab === "orders" && customerOrders.data.length > 0 && (
          <section className="p-3 my-5">
            <Pagination
              length={customerOrders.data.length}
              itemsPerPage={20}
              handlePagination={(page) => setCustomerOrdersPage(page)}
              currentPage={customerOrdersPage}
              prevPage={() =>
                setCustomerOrdersPage((prev) => Math.max(1, prev - 1))
              }
              nextPage={() =>
                setCustomerOrdersPage((prev) =>
                  Math.min(Math.ceil(customerOrders.data.length / 20), prev + 1)
                )
              }
            />
          </section>
        )}

        {activeTab === "updated" && updatedProducts.data.length > 0 && (
          <section className="p-3 my-5">
            <Pagination
              length={updatedProducts.data.length}
              itemsPerPage={50}
              handlePagination={(page) => setUpdatedProductsPage(page)}
              currentPage={updatedProductsPage}
              prevPage={() =>
                setUpdatedProductsPage((prev) => Math.max(1, prev - 1))
              }
              nextPage={() =>
                setUpdatedProductsPage((prev) =>
                  Math.min(
                    Math.ceil(updatedProducts.data.length / 50),
                    prev + 1
                  )
                )
              }
            />
          </section>
        )}
      </div>

      {confirmNeededPurchase ? (
        <ConfirmPurchaseModal
          open
          productName={confirmNeededPurchase.product.product_name}
          onCancel={() => setConfirmNeededPurchase(null)}
          isSubmitting={updateQuantity.status === "loading"}
          onConfirm={async () => {
            const { product, quantityNeeded } = confirmNeededPurchase;
            try {
              await dispatch(
                updateProductQuantity({
                  productId: product.product_id,
                  newQuantity: quantityNeeded,
                })
              ).unwrap();
              await dispatch(
                updateProductStatus({
                  productId: product.product_id,
                  isAvailable: true,
                })
              ).unwrap();
              const at = new Date().toISOString();
              setRecentNeededPurchases((prev) =>
                [
                  {
                    key: `${product.product_id}-${at}`,
                    productId: product.product_id,
                    productName: product.product_name,
                    quantity: quantityNeeded,
                    purchasedAt: at,
                  },
                  ...prev,
                ].slice(0, RECENT_NEEDED_MAX)
              );
              setConfirmNeededPurchase(null);
            } catch {
              /* thunk surfaces toast */
            }
          }}
        />
      ) : null}

      {/* Customer Order Details Modal */}
      {showOrderDetailsModal && selectedCustomerOrder && (
        <div className="fixed z-40 inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div
            className="bg-white p-6 rounded-md"
            style={{ width: "800px", maxHeight: "90vh", overflow: "auto" }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Customer Order Details</h2>
              <button
                onClick={closeOrderDetailsModal}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Customer Info */}
            <div className="mb-4 p-4 bg-gray-50 rounded">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Customer Name</p>
                  <p className="font-semibold">
                    {selectedCustomerOrder.userName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold">{selectedCustomerOrder.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    Total Products Ordered
                  </p>
                  <p className="font-semibold text-[#B5572C]">
                    {selectedCustomerOrder.products.length}
                  </p>
                </div>
              </div>
            </div>

            {/* Products Table */}
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left text-sm">S/N</th>
                    <th className="p-2 text-left text-sm">Product Name</th>
                    <th className="p-2 text-center text-sm">Quantity</th>
                    <th className="p-2 text-left text-sm">Order Date</th>
                    <th className="p-2 text-left text-sm">Order ID</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCustomerOrder.products.map(
                    (product: any, index: number) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="p-2 text-sm text-gray-600">
                          {index + 1}
                        </td>
                        <td className="p-2 text-sm">{product.productName}</td>
                        <td className="p-2 text-sm text-center font-semibold">
                          {product.quantityBought}
                        </td>
                        <td className="p-2 text-sm text-nowrap">
                          {formatDate(product.orderDate)}
                        </td>
                        <td className="p-2 text-xs font-mono">
                          {product.orderId}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>

            {/* Close Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={closeOrderDetailsModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </main>
  );
};

export default SalesInventory;
